import { FC, memo, useMemo } from 'react'
import { Controller } from 'react-hook-form'
import filtersForm from '../../../../forms/filtersForm'
import CategoryGrid from '../CategoryGrid'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import ClassificationGrid from '../ClassificationGrid/ClassificationGrid'
import ApplicationTypeGrid from '../ApplicationTypeGrid'
import ApplicationStatusGrid from '../ApplicationStatusGrid'
import CompanyTypeGrid from '../CompanyTypeGrid'
import EstimatedProjectValueBandGrid from '../EstimatedProjectValueBandGrid'
import FilterSummary from '../FilterSummary'
import { useFiltersForm } from '../../../../forms/filtersForm/useFiltersForm'
import { ArrayToggleController } from '../../../form/ArrayToggleController'
import PlanningRecordExamplesPanel from '../PlanningRecordExamplesPanel'

const EditFiltersPanel: FC<{ onSave?: () => void }> = ({ onSave }) => {
  const {
    control,
    getPlanningCategoriesNextValue,
    handleSubmit,
    onSubmit,
    formState,
    formCompanyType,
    formPlanningCategories,
    formPlanningClassifications,
    formPlanningApplicationTypes,
    formPlanningApplicationStatuses,
    formEstimatedProjectValueBands,
  } = useFiltersForm({
    onSubmitSuccess: () => {
      if (onSave) {
        onSave()
      }
    },
  })
  const planningRecordExamplesFilters = useMemo(
    () => ({
      planningApplicationStatuses: formPlanningApplicationStatuses,
      planningApplicationTypes: formPlanningApplicationTypes,
      estimatedProjectValueBands: formEstimatedProjectValueBands,
      planningClassifications: formPlanningClassifications,
      planningCategories: formPlanningCategories,
    }),
    [
      formEstimatedProjectValueBands,
      formPlanningApplicationStatuses,
      formPlanningApplicationTypes,
      formPlanningCategories,
      formPlanningClassifications,
    ],
  )
  return (
    <Box
      component="form"
      id="edit-filters-panel"
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        flex: 1,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={theme => ({
          overflow: 'scroll',
          width: '100%',
          paddingBottom: '120px',
          [theme.breakpoints.down('sm')]: {
            paddingTop: '70px',
            paddingBottom: '140px',
          },
        })}
      >
        <Box
          sx={{
            paddingLeft: 2,
            paddingRight: 2,
            paddingTop: 2,
          }}
        >
          <Typography variant="h6" component={'span'}>
            Select your business type
          </Typography>
        </Box>
        <Controller
          name="companyType"
          control={control}
          render={({ field }) => (
            <CompanyTypeGrid companyType={field.value} onChange={field.onChange} />
          )}
          rules={{
            validate: filtersForm.validateCompanyTypeField,
          }}
        />

        {formCompanyType && (
          <>
            <Box
              sx={{
                paddingLeft: 2,
                paddingRight: 2,
                paddingTop: 2,
              }}
            >
              <Typography variant="h6" component={'span'}>
                When to send letters{' '}
                <Typography variant="body1" component={'span'}>
                  (select all applicable)
                </Typography>
              </Typography>
            </Box>
            <ArrayToggleController
              name="planningApplicationStatuses"
              control={control}
              render={({ field }) => (
                <ApplicationStatusGrid
                  planningApplicationStatuses={field.value}
                  onChange={field.onChange}
                />
              )}
              rules={{
                validate: filtersForm.validatePlanningApplicationStatusesField,
              }}
            />
          </>
        )}
        {formCompanyType && (
          <>
            <Box
              sx={{
                paddingLeft: 2,
                paddingRight: 2,
                paddingTop: 2,
              }}
            >
              <Typography variant="h6" component={'span'}>
                Select application types{' '}
                <Typography variant="body1" component={'span'}>
                  (select all applicable)
                </Typography>
              </Typography>
            </Box>
            <ArrayToggleController
              name="planningApplicationTypes"
              control={control}
              render={({ field }) => (
                <ApplicationTypeGrid
                  companyType={formCompanyType}
                  planningApplicationTypes={field.value}
                  onChange={field.onChange}
                />
              )}
              rules={{
                validate: filtersForm.validatePlanningApplicationTypesField,
              }}
            />
          </>
        )}
        {formCompanyType && (
          <>
            <Box
              sx={{
                paddingLeft: 2,
                paddingRight: 2,
                paddingTop: 2,
              }}
            >
              <Typography variant="h6" component={'span'}>
                Select applications about{' '}
                <Typography variant="body1" component={'span'}>
                  (select all applicable)
                </Typography>
              </Typography>
            </Box>
            <Controller
              name="planningClassifications"
              control={control}
              render={({ field }) => (
                <ClassificationGrid
                  companyType={formCompanyType}
                  planningClassifications={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
              )}
              rules={{
                validate: filtersForm.validatePlanningClassificationsField,
              }}
            />
          </>
        )}
        {formCompanyType && (
          <>
            <Box
              sx={{
                paddingLeft: 2,
                paddingRight: 2,
                paddingTop: 2,
              }}
            >
              <Typography variant="h6" component={'span'}>
                Select work categories{' '}
                <Typography variant="body1" component={'span'}>
                  (select all applicable)
                </Typography>
              </Typography>
            </Box>
            <ArrayToggleController
              name="planningCategories"
              control={control}
              getNextValue={getPlanningCategoriesNextValue}
              render={({ field }) => (
                <CategoryGrid
                  companyType={formCompanyType}
                  planningCategories={field.value}
                  planningClassifications={formPlanningClassifications}
                  onChange={field.onChange}
                />
              )}
              rules={{
                validate: filtersForm.validatePlanningCategoriesField,
              }}
            />
          </>
        )}
        {formCompanyType && (
          <>
            <Box
              sx={{
                paddingLeft: 2,
                paddingRight: 2,
                paddingTop: 2,
              }}
            >
              <Typography variant="h6" component={'span'}>
                Select project value bands{' '}
                <Typography variant="body1" component={'span'}>
                  (select all applicable)
                </Typography>
              </Typography>
            </Box>
            <ArrayToggleController
              name="estimatedProjectValueBands"
              control={control}
              render={({ field }) => (
                <EstimatedProjectValueBandGrid
                  estimatedProjectValueBands={field.value}
                  onChange={field.onChange}
                />
              )}
              rules={{
                validate: filtersForm.validateEstimatedProjectValueBandsField,
              }}
            />
          </>
        )}
        <FilterSummary
          companyType={formCompanyType}
          planningApplicationTypes={formPlanningApplicationTypes}
          planningApplicationStatuses={formPlanningApplicationStatuses}
          planningClassifications={formPlanningClassifications}
          planningCategories={formPlanningCategories}
          estimatedProjectValueBands={formEstimatedProjectValueBands}
        />
        {/* <PlanningRecordExamplesPanel filters={planningRecordExamplesFilters} /> */}
      </Box>
      <Box
        sx={theme => ({
          position: 'absolute',
          bottom: 30,
          right: 30,
          borderRadius: 1,
          [theme.breakpoints.down('sm')]: {
            pointerEvents: 'none',
            bottom: 0,
            right: 0,
            left: 0,
          },
        })}
      >
        <Box
          sx={theme => ({
            position: 'relative',
            flex: 1,
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-end',
            [theme.breakpoints.down('sm')]: {
              paddingTop: 2,
              paddingBottom: 2,
              paddingRight: 2,
              paddingLeft: 1,
            },
          })}
        >
          <Box
            sx={theme => ({
              position: 'absolute',
              top: 0,
              right: 0,
              left: 0,
              bottom: 0,
              [theme.breakpoints.down('sm')]: {
                backgroundColor: 'pageBackground',
                maskImage: 'linear-gradient(to top, rgba(0,0,0,1), rgba(0,0,0,0))',
              },
            })}
          />
          <Box
            sx={{
              marginRight: 2,
            }}
          >
            {formState.errors.root?.message && (
              <Typography color="error" variant="body2">
                {formState.errors.root.message}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              backgroundColor: 'pageBackground',
            }}
          >
            <Button
              sx={{
                pointerEvents: 'visible',
              }}
              id="edit-filters-save"
              variant="contained"
              type="submit"
              disabled={
                formState.isSubmitting ||
                !formCompanyType ||
                !formPlanningApplicationStatuses.length ||
                !formPlanningApplicationTypes.length ||
                !formPlanningClassifications.length ||
                !formPlanningCategories.length ||
                !formEstimatedProjectValueBands.length
              }
            >
              Save
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default memo(EditFiltersPanel)
